import { useContext, useEffect } from "react";
import { Offcanvas } from "react-bootstrap";
import { Formik, Form as FormFormik } from "formik";
import * as Yup from "yup";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import Form from "../../Helpers/Form/Form";
import FormHeader from "../../Helpers/Form/FormParts/FormHeader";
import Button from "../../Helpers/Button/Button";
import trans, { isRTL } from "../../../utils";
import { popupcontext } from "../../../context/Popupscontext";
import {
	useCreateRecurringPlannerMutation,
	useEditRecurringPlannerMutation,
	useGetSingleRecurringPlannerQuery,
} from "../../../Redux/service/RecurringPlanners/RecurringPlanners";
import RecurringPlannerFormBody from "./RecurringPlannerFormBody";
import { normalizeDays } from "../WeekDayChips";

export default function RecurringPlannerForm() {
	const { form, isEditing, currentPlannerId, setEdited, handleClose } =
		useContext(popupcontext);
	const [createPlanner] = useCreateRecurringPlannerMutation();
	const [editPlanner] = useEditRecurringPlannerMutation();
	const { refetch, data: editData } = useGetSingleRecurringPlannerQuery(
		currentPlannerId,
		{ skip: !currentPlannerId }
	);

	useEffect(() => {
		if (isEditing && currentPlannerId) {
			refetch();
		}
	}, [isEditing, form]);

	let initialValues;
	if (isEditing && editData?.data) {
		const data = editData.data;
		initialValues = {
			name: data?.name || "",
			type: data?.type || "",
			priority: data?.priority || "medium",
			expectedDuration: data?.expectedDuration || "",
			startDate: data?.startDate ? dayjs(data.startDate).format("YYYY-MM-DD") : "",
			endDate: data?.endDate ? dayjs(data.endDate).format("YYYY-MM-DD") : "",
			startTime: data?.startTime ? data.startTime.slice(0, 5) : "",
			daysOfWeek: normalizeDays(data?.daysOfWeek),
			assignedToId: data?.assignedToId ? String(data.assignedToId) : "",
			poiId: data?.poiId || "",
			forms: data?.forms?.map((f) => f.formId) || [],
			notes: data?.notes || "",
			flowType: data?.isFlexible ? "flexible" : "sequential",
		};
	} else {
		initialValues = {
			name: "",
			type: "",
			priority: "medium",
			expectedDuration: "",
			startDate: "",
			endDate: "",
			startTime: "",
			daysOfWeek: [],
			assignedToId: "",
			poiId: "",
			forms: [],
			notes: "",
			flowType: "sequential",
		};
	}

	const validationSchema = Yup.object({
		name: Yup.string()
			.required(trans("Planner Name is required"))
			.matches(/^[^\s]/, trans("cannot start with a space"))
			.min(3, trans("Planner Name must be at least 3 characters"))
			.max(128, trans("Planner Name cannot exceed 128 characters")),
		type: Yup.string().required(trans("Task type is required")),
		priority: Yup.string().required(trans("Priority is required")),
		expectedDuration: Yup.number()
			.typeError(trans("Duration must be a number"))
			.required(trans("Duration is required"))
			.min(1, trans("Duration must be a positive number"))
			.max(1440, trans("Duration cannot exceed 1440 minutes")),
		startDate: Yup.string()
			.required(trans("Start date is required"))
			.test(
				"not-in-past",
				trans("Start date cannot be in the past"),
				function (value) {
					if (!value) return true;
					if (isEditing) return true;
					return !dayjs(value).isBefore(dayjs(), "day");
				}
			),
		endDate: Yup.string()
			.nullable()
			.test(
				"after-start",
				trans("End date must be after start date"),
				function (value) {
					const { startDate } = this.parent;
					if (!value || !startDate) return true;
					return !dayjs(value).isBefore(dayjs(startDate), "day");
				}
			),
		startTime: Yup.string().required(trans("Start time is required")),
		daysOfWeek: Yup.array()
			.of(Yup.number().min(0).max(6))
			.min(1, trans("Select at least one day of the week")),
		assignedToId: Yup.string().nullable(),
		poiId: Yup.number().nullable(),
		forms: Yup.array().of(Yup.number()).nullable(),
	});

	const notify = (message, type = "success") => {
		toast[type](message, {
			autoClose: 800,
			rtl: localStorage.getItem("lang") === "ar" ? true : false,
		});
	};

	const handleSubmit = async (values, setSubmitting, resetForm) => {
		setSubmitting(true);

		const normalizedValues = {
			name: values.name,
			type: values.type,
			priority: values.priority,
			expectedDuration: Number(values.expectedDuration),
			startDate: values.startDate,
			endDate: values.endDate || null,
			startTime: values.startTime,
			daysOfWeek: [...values.daysOfWeek].sort((a, b) => a - b),
			assignedToId: values.assignedToId ? Number(values.assignedToId) : null,
			poiId: values.poiId ? Number(values.poiId) : null,
			forms: values.forms.map((id) => Number(id)),
			notes: values.notes || null,
			isFlexible: values.flowType === "flexible",
		};

		let payload = normalizedValues;
		if (isEditing && editData?.data) {
			const data = editData.data;
			const original = {
				name: data?.name || "",
				type: data?.type || "",
				priority: data?.priority || "medium",
				expectedDuration: data?.expectedDuration || "",
				startDate: data?.startDate
					? dayjs(data.startDate).format("YYYY-MM-DD")
					: "",
				endDate: data?.endDate ? dayjs(data.endDate).format("YYYY-MM-DD") : null,
				startTime: data?.startTime ? data.startTime.slice(0, 5) : "",
				daysOfWeek: [...normalizeDays(data?.daysOfWeek)].sort((a, b) => a - b),
				assignedToId: data?.assignedToId || null,
				poiId: data?.poiId || null,
				forms: data?.forms?.map((f) => f.formId) || [],
				notes: data?.notes || null,
				isFlexible: data?.isFlexible ?? true,
			};
			payload = Object.keys(normalizedValues).reduce((acc, key) => {
				const newVal = normalizedValues[key];
				const oldVal = original[key];
				if (Array.isArray(newVal)) {
					if (JSON.stringify(newVal) !== JSON.stringify(oldVal)) {
						acc[key] = newVal;
					}
				} else if (newVal !== oldVal) {
					acc[key] = newVal;
				}
				return acc;
			}, {});
		}

		try {
			if (!isEditing) {
				await createPlanner(payload).unwrap();
				notify(trans("Recurring planner created successfully"));
			} else {
				if (Object.keys(payload).length === 0) {
					notify(trans("No changes to save"), "info");
					setSubmitting(false);
					return;
				}
				await editPlanner({ id: currentPlannerId, payload }).unwrap();
				notify(trans("Recurring planner updated successfully"));
			}
			resetForm();
			handleClose("form");
			setEdited(true);
		} catch (err) {
			const apiError =
				err?.data?.message ??
				err?.data?.errors?.[0]?.msg ??
				"Something went wrong";
			notify(trans(apiError), "error");
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<Form show={form}>
			<Offcanvas.Header>
				<FormHeader
					title={
						isEditing
							? trans("Edit Recurring Planner")
							: trans("Add Recurring Planner")
					}
				/>
			</Offcanvas.Header>
			<div className="H-line"></div>
			<Offcanvas.Body>
				<Formik
					initialValues={initialValues}
					enableReinitialize={true}
					onSubmit={(values, { setSubmitting, resetForm }) =>
						handleSubmit(values, setSubmitting, resetForm)
					}
					validationSchema={validationSchema}
				>
					{({ handleSubmit, isSubmitting }) => (
						<FormFormik onSubmit={handleSubmit} dir={!isRTL ? "ltr" : "rtl"}>
							<RecurringPlannerFormBody isEditing={isEditing} />
							<div className="offcanvas-footer m-0">
								<div className="offcanvas-footer-body gap-8">
									<Button
										type={"primary"}
										htmlType="submit"
										loading={isSubmitting}
										disabled={isSubmitting}
									>
										{isSubmitting ? trans("Saving...") : trans("Save")}
									</Button>
									<Button
										type={"default"}
										htmlType="button"
										disabled={isSubmitting}
										onClick={() => handleClose("form")}
									>
										{trans("Cancel")}
									</Button>
								</div>
							</div>
						</FormFormik>
					)}
				</Formik>
			</Offcanvas.Body>
		</Form>
	);
}
